/**
 * Unified Discipline Plugin Registry
 *
 * This registry synchronizes with the backend plugin system.
 * The backend is the SINGLE SOURCE OF TRUTH for all discipline configurations.
 *
 * Frontend plugins act as UI adapters that provide:
 * - Rendering configurations
 * - Localized labels
 * - UI-specific helpers
 *
 * Data (competition types, staff roles, etc.) comes exclusively from the backend.
 */

// =============================================================================
// FE↔BE CORE-COMPATIBILITY VERSIONING
// =============================================================================

/**
 * The plugin-contract version this FE build was compiled against.
 * Bump the MAJOR when the FE↔BE plugin contract changes in a breaking way.
 * Compatibility is checked by MAJOR version only (semver).
 */
export const EXPECTED_CORE_VERSION = '3.0.0';

/** Parsed semver triple.  Returns null for any unparseable string. */
function parseSemver(version: string): { major: number; minor: number; patch: number } | null {
  const match = /^(\d+)\.(\d+)\.(\d+)/.exec(version.trim());
  if (!match) return null;
  return {
    major: parseInt(match[1], 10),
    minor: parseInt(match[2], 10),
    patch: parseInt(match[3], 10),
  };
}

/** Returns true when the two version strings share the same MAJOR number. */
function isMajorCompatible(a: string, b: string): boolean {
  const pa = parseSemver(a);
  const pb = parseSemver(b);
  if (!pa || !pb) return false;
  return pa.major === pb.major;
}

export interface CoreCompatibilityIssue {
  code: string;
  reason: string;
}

export interface CoreCompatibilityVerdict {
  /** The version the FE was built against. */
  expected: string;
  /** The core_version reported by the BE (null = older BE that does not expose it). */
  beCore: string | null;
  /** Overall verdict: true when no discipline has a compatibility problem. */
  compatible: boolean;
  /** Per-discipline issues (empty when fully compatible). */
  issues: CoreCompatibilityIssue[];
}

import {
  competitionPluginService,
  BackendSportDiscipline,
  BackendCompetitionType,
  BackendSquadType,
  BackendMovementType,
  BackendStaffRole,
  BackendPlayerCategory,
  BackendCompetitionLevel,
  BackendDisciplineUIConfig
} from '../../services/competitionPluginService';
import {
  DisciplinePlugin,
  DisciplinePluginMetadata,
  CompetitionTypeConfig,
  SquadTypeConfig,
  MovementTypeConfig,
  CompetitionDetailConfig,
  NavigationCardConfig,
  BookingTerminology,
  StaffRoleConfig,
  MembershipCategoryConfig,
  PlayerCategoryGroupConfig,
  CompetitionLevelConfig,
  ScoringSystemConfig,
  RankingSystemConfig,
  PairingStrategyConfig,
  CompetitionFormatConfig,
  ParticipantType,
  CompetitionTypeStatus,
  HierarchyLevel,
  ValidationResult,
  ValidationError,
  ValidationWarning
} from './types';

// =============================================================================
// BACKEND-SYNCED PLUGIN ADAPTER
// =============================================================================

/**
 * Adapter that wraps backend discipline data to implement the frontend plugin interface.
 * This ensures the frontend always uses data from the backend.
 */
export class BackendDisciplineAdapter implements DisciplinePlugin {
  private discipline: BackendSportDiscipline;
  private uiConfig: DisciplineUIConfig;
  private _competitionTypes: CompetitionTypeConfig[] | null = null;
  private _squadTypes: SquadTypeConfig[] | null = null;
  private _movementTypes: MovementTypeConfig[] | null = null;

  constructor(discipline: BackendSportDiscipline, overrides?: Partial<DisciplineUIConfig>) {
    this.discipline = discipline;
    // Build the UI config from the backend ui_config (single source of truth).
    // `overrides` (e.g. iconComponent / customValidation) are React-side extras
    // that the backend cannot provide.
    const built = this.buildUIConfigFromBackend(discipline.ui_config);
    this.uiConfig = {
      ...built,
      iconComponent: overrides?.iconComponent,
      customValidation: overrides?.customValidation,
    };
  }

  /**
   * Map the backend ui_config (snake_case) into the frontend DisciplineUIConfig
   * (camelCase), applying generic fallbacks for any missing field.
   */
  private buildUIConfigFromBackend(beConfig?: BackendDisciplineUIConfig): DisciplineUIConfig {
    const color = beConfig?.color || '#3b82f6';

    const beTerm = beConfig?.terminology;
    const terminology: CompetitionDetailConfig['terminology'] = {
      competitionSingular: beTerm?.competition_singular || 'Competizione',
      competitionPlural: beTerm?.competition_plural || 'Competizioni',
      participantSingular: beTerm?.participant_singular || 'Partecipante',
      participantPlural: beTerm?.participant_plural || 'Partecipanti',
      matchSingular: beTerm?.match_singular || 'Partita',
      matchPlural: beTerm?.match_plural || 'Partite',
      resultLabel: beTerm?.result_label || 'Classifica',
    };

    const beBooking = beConfig?.booking_terminology;
    const bookingTerminology: BookingTerminology = {
      sectionTitle: beBooking?.section_title || 'Prenotazioni',
      totalLabel: beBooking?.total_label || 'Prenotazioni Totali',
      confirmedLabel: beBooking?.confirmed_label || 'Confermati',
      pendingLabel: beBooking?.pending_label || 'In Attesa',
      singleAvailableLabel: beBooking?.single_available_label || 'Disponibili',
      infoTitle: beBooking?.info_title || 'Informazioni Prenotazioni',
      openLabel: beBooking?.open_label || 'Apertura Prenotazioni',
      closeLabel: beBooking?.close_label || 'Chiusura Prenotazioni',
      notesLabel: beBooking?.notes_label || 'Note Prenotazioni',
    };

    const navigationCards: NavigationCardConfig[] | undefined = beConfig?.navigation_cards
      && beConfig.navigation_cards.length > 0
      ? beConfig.navigation_cards.map(nc => ({
          id: nc.id,
          icon: nc.icon,
          label: nc.label,
          description: nc.description,
          href: nc.href,
          visibleTo: nc.visible_to,
          order: nc.order,
        }))
      : undefined;

    const beSections = beConfig?.sections;
    const sections: CompetitionDetailConfig['sections'] | undefined = beSections
      ? {
          showBookingInline: beSections.show_booking_inline,
          showStaff: beSections.show_staff,
          showManagers: beSections.show_managers,
          showPrizes: beSections.show_prizes,
          showParticipationFees: beSections.show_participation_fees,
        }
      : undefined;

    const icon = beConfig?.discipline_icon || this.discipline.icon || this.getDefaultIcon();

    const competitionDetailConfig: CompetitionDetailConfig = {
      terminology,
      bookingTerminology,
      themeColor: color,
      disciplineIcon: icon,
      navigationCards,
      sections,
    };

    return {
      color,
      competitionDetailConfig,
    };
  }

  getMetadata(): DisciplinePluginMetadata {
    return {
      code: this.discipline.code,
      name: this.discipline.name,
      // Icon and color come from the backend ui_config (single source of truth),
      // with generic fallbacks handled in buildUIConfigFromBackend.
      version: this.discipline.implementation_version || '1.0.0',
      description: this.discipline.description,
      icon: this.uiConfig.competitionDetailConfig?.disciplineIcon
        || this.discipline.icon
        || this.getDefaultIcon(),
      color: this.uiConfig.color,
      // Derivato dal backend (§5.7): mai dedotto qui dal codice disciplina.
      playSurface: this.discipline.play_surface ?? false,
      playSurfaceLabel: this.discipline.play_surface_label ?? null,
    };
  }

  getCompetitionTypes(): CompetitionTypeConfig[] {
    if (!this._competitionTypes) {
      this._competitionTypes = this.discipline.competition_types.map(tt => this.mapCompetitionType(tt));
    }
    return this._competitionTypes;
  }

  getCompetitionType(code: string): CompetitionTypeConfig | undefined {
    return this.getCompetitionTypes().find(tt => tt.code === code);
  }

  getSquadTypes(): SquadTypeConfig[] {
    if (!this._squadTypes) {
      this._squadTypes = (this.discipline.squad_types || []).map(st => this.mapSquadType(st));
    }
    return this._squadTypes;
  }

  /**
   * Get movement types for this discipline.
   * Backend (plugin.get_movement_types()) is the single source of truth.
   * Bridge returns 6 items (mitchell_standard/skip/relay, howell_complete/partial, web_mitchell).
   * Other disciplines return [].
   */
  getMovementTypes(): MovementTypeConfig[] {
    if (!this._movementTypes) {
      this._movementTypes = (this.discipline.movement_types || []).map(mt => this.mapMovementType(mt));
    }
    return this._movementTypes;
  }

  getAvailableCompetitionTypes(): CompetitionTypeConfig[] {
    return this.getCompetitionTypes().filter(tt =>
      tt.status === CompetitionTypeStatus.AVAILABLE ||
      tt.status === CompetitionTypeStatus.BETA
    );
  }

  getCompetitionTypeLabel(code: string): string {
    const tt = this.getCompetitionType(code);
    return tt?.name || code;
  }

  getParticipantTypeLabel(type: ParticipantType): string {
    const labels: Record<ParticipantType, string> = {
      [ParticipantType.INDIVIDUAL]: 'Individuale',
      [ParticipantType.PAIR]: 'Coppie',
      [ParticipantType.TEAM]: 'Squadre'
    };
    return labels[type] || type;
  }

  getStaffRoles(): StaffRoleConfig[] {
    // Backend is the SINGLE SOURCE OF TRUTH. Do NOT invent domain data.
    if (this.discipline.staff_roles && this.discipline.staff_roles.length > 0) {
      return this.discipline.staff_roles.map(sr => this.mapStaffRole(sr));
    }
    console.warn(`[plugin] discipline ${this.discipline.code}: backend returned empty staff_roles — misconfigured plugin?`);
    return [];
  }

  getMembershipCategories(): MembershipCategoryConfig[] {
    // Backend is the SINGLE SOURCE OF TRUTH. Do NOT invent domain data.
    if (this.discipline.membership_categories && this.discipline.membership_categories.length > 0) {
      return this.discipline.membership_categories.map(pc => this.mapMembershipCategory(pc));
    }
    console.warn(`[plugin] discipline ${this.discipline.code}: backend returned empty membership_categories — misconfigured plugin?`);
    return [];
  }

  getPlayerCategoryGroups(): PlayerCategoryGroupConfig[] {
    // Category groups are fetched via dedicated API endpoint
    // This returns empty by default; use the API hook for actual data
    return [];
  }

  getCompetitionLevels(): CompetitionLevelConfig[] {
    // Backend is the SINGLE SOURCE OF TRUTH. Do NOT invent domain data.
    if (this.discipline.competition_levels && this.discipline.competition_levels.length > 0) {
      return this.discipline.competition_levels.map(cl => this.mapCompetitionLevel(cl));
    }
    console.warn(`[plugin] discipline ${this.discipline.code}: backend returned empty competition_levels — misconfigured plugin?`);
    return [];
  }

  getRankingSystems(): RankingSystemConfig[] {
    return (this.discipline.ranking_systems ?? []).map(rs => ({
      code: rs.code,
      name: rs.name,
      description: rs.description,
      method: rs.method,
      pointsPerWin: rs.points_per_win,
      pointsPerDraw: rs.points_per_draw,
      pointsPerLoss: rs.points_per_loss,
      initialRating: rs.initial_rating,
      kFactor: rs.k_factor,
      minGamesForRanking: rs.min_games_for_ranking,
      icon: rs.icon ?? undefined,
    }));
  }

  getPairingStrategies(): PairingStrategyConfig[] {
    return (this.discipline.pairing_strategies ?? []).map(ps => ({
      code: ps.code,
      name: ps.name,
      description: ps.description,
      firstDayStrategy: ps.first_day_strategy,
      subsequentDaysStrategy: ps.subsequent_days_strategy,
      useHistoricalRanking: ps.use_historical_ranking,
      skillBalanceTolerance: ps.skill_balance_tolerance,
      avoidRepeatPairings: ps.avoid_repeat_pairings,
      repeatPairingCooldownDays: ps.repeat_pairing_cooldown_days,
      icon: ps.icon ?? undefined,
    }));
  }

  getCompetitionFormats(): CompetitionFormatConfig[] {
    return (this.discipline.competition_formats ?? []).map(cf => ({
      code: cf.code,
      name: cf.name,
      description: cf.description,
      formatType: cf.format_type,
      hasSeasons: cf.has_seasons,
      dynamicPairing: cf.dynamic_pairing,
      minDaysPerSeason: cf.min_days_per_season,
      maxDaysPerSeason: cf.max_days_per_season ?? undefined,
      rankingCarryover: cf.ranking_carryover,
      carryoverPercentage: cf.carryover_percentage,
      icon: cf.icon ?? undefined,
    }));
  }

  getScoringSystems(): ScoringSystemConfig[] {
    // PLUGIN-FIRST: Use backend scoring systems if available (from plugin)
    if (this.discipline.scoring_systems && this.discipline.scoring_systems.length > 0) {
      return this.discipline.scoring_systems.map(ss => ({
        code: ss.code,
        name: ss.name,
        description: ss.description,
        icon: ss.icon || undefined,
        scoringType: ss.scoring_type,
        isHigherBetter: ss.is_higher_better,
        formatTemplate: ss.format_template,
        displayOrder: ss.display_order,
      }));
    }

    // Fallback: Extract from competition types (legacy)
    const systems = new Set<string>();
    this.discipline.competition_types.forEach(tt => {
      tt.scoring_systems.forEach(s => systems.add(s));
    });

    return Array.from(systems).map(code => ({
      code,
      name: this.getScoringSystemLabel(code),
      description: this.getScoringSystemDescription(code)
    }));
  }

  validateCompetition(config: Record<string, unknown>): ValidationResult {
    const errors: ValidationError[] = [];
    const warnings: ValidationWarning[] = [];

    const competitionTypeCode = config.competition_type_code as string;
    const competitionType = this.discipline.competition_types.find(
      tt => tt.code === competitionTypeCode
    );

    if (!competitionType) {
      errors.push({
        field: 'competition_type_code',
        message: 'Tipo competizione non trovato',
        code: 'INVALID_COMPETITION_TYPE'
      });
      return { valid: false, errors, warnings };
    }

    const participants = config.max_participants as number;
    if (participants !== undefined) {
      if (participants < competitionType.min_participants) {
        errors.push({
          field: 'max_participants',
          message: `Minimo ${competitionType.min_participants} partecipanti richiesti`,
          code: 'MIN_PARTICIPANTS'
        });
      }
      if (competitionType.max_participants && participants > competitionType.max_participants) {
        warnings.push({
          field: 'max_participants',
          message: `Massimo consigliato: ${competitionType.max_participants}`,
          code: 'MAX_PARTICIPANTS_EXCEEDED'
        });
      }
    }

    const scoringSystem = config.scoring_system as string;
    if (scoringSystem && !competitionType.scoring_systems.includes(scoringSystem)) {
      errors.push({
        field: 'scoring_system',
        message: `Sistema ${scoringSystem} non supportato`,
        code: 'INVALID_SCORING_SYSTEM'
      });
    }

    if (this.uiConfig.customValidation) {
      const customResult = this.uiConfig.customValidation(config, competitionType);
      errors.push(...customResult.errors);
      warnings.push(...customResult.warnings);
    }

    return { valid: errors.length === 0, errors, warnings };
  }

  // =========================================================================
  // COMPETITION DETAIL CONFIGURATION
  // =========================================================================

  getCompetitionDetailConfig(): CompetitionDetailConfig {
    const meta = this.getMetadata();
    const defaultBooking = {
      sectionTitle: 'Prenotazioni',
      totalLabel: 'Prenotazioni Totali',
      confirmedLabel: 'Confermate',
      pendingLabel: 'In Attesa',
      singleAvailableLabel: 'Singoli Disponibili',
      infoTitle: 'Informazioni Prenotazioni',
      openLabel: 'Apertura Prenotazioni',
      closeLabel: 'Chiusura Prenotazioni',
      notesLabel: 'Note Prenotazioni',
    };
    const defaults: CompetitionDetailConfig = {
      terminology: {
        competitionSingular: 'Competizione',
        competitionPlural: 'Competizioni',
        participantSingular: 'Partecipante',
        participantPlural: 'Partecipanti',
        matchSingular: 'Partita',
        matchPlural: 'Partite',
        resultLabel: 'Risultati',
      },
      bookingTerminology: defaultBooking,
      themeColor: this.uiConfig.color || meta.color || '#3b82f6',
      disciplineIcon: meta.icon || '🏅',
    };
    // Merge with discipline-specific overrides
    const overrides = this.uiConfig.competitionDetailConfig;
    if (overrides) {
      return {
        ...defaults,
        ...overrides,
        terminology: { ...defaults.terminology, ...overrides.terminology },
        bookingTerminology: { ...defaultBooking, ...overrides.bookingTerminology },
        sections: { ...defaults.sections, ...overrides.sections },
      };
    }
    return defaults;
  }

  // =========================================================================
  // PRIVATE HELPER METHODS
  // =========================================================================

  private mapStaffRole(sr: BackendStaffRole): StaffRoleConfig {
    return {
      code: sr.role_code,
      name: sr.role_name,
      name_plural: sr.role_name_plural,
      description: sr.description,
      is_required: sr.required_for_competition,
      min_per_competition: sr.min_required,
      max_per_competition: sr.max_allowed,
      can_be_player: sr.can_be_player || false,
      icon: sr.icon
    };
  }

  private mapMembershipCategory(pc: BackendPlayerCategory): MembershipCategoryConfig {
    return {
      code: pc.category_code,
      name: pc.category_name,
      description: pc.description,
      is_official: pc.can_participate_official,
      earns_points: pc.can_earn_points,
      age_min: pc.age_min,
      age_max: pc.age_max,
      icon: pc.icon
    };
  }

  private mapCompetitionLevel(cl: BackendCompetitionLevel): CompetitionLevelConfig {
    return {
      code: cl.level,
      name: cl.name,
      description: cl.description,
      hierarchy_level: this.mapHierarchyLevel(cl.level),
      points_multiplier: cl.ranking_multiplier,
      requires_homologation: cl.requires_homologation,
      icon: cl.icon
    };
  }

  private mapHierarchyLevel(level: string): HierarchyLevel {
    const map: Record<string, HierarchyLevel> = {
      circolo: HierarchyLevel.CIRCOLO,
      provincial: HierarchyLevel.PROVINCIAL,
      regional: HierarchyLevel.REGIONAL,
      national: HierarchyLevel.NATIONAL,
      international: HierarchyLevel.INTERNATIONAL
    };
    return map[level.toLowerCase()] || HierarchyLevel.CIRCOLO;
  }

  private mapCompetitionType(bt: BackendCompetitionType): CompetitionTypeConfig {
    return {
      code: bt.code,
      name: bt.name,
      description: bt.description,
      participant_type: this.mapParticipantType(bt.participant_type),
      min_participants: bt.min_participants,
      max_participants: bt.max_participants || undefined,
      status: this.mapStatus(bt.status),
      scoring_systems: bt.scoring_systems,
      default_scoring_system: bt.default_scoring,
      boards_per_round: bt.boards_per_round,
      features: bt.features || {},
      is_team_event: bt.participant_type === 'team'
    };
  }

  private mapSquadType(st: BackendSquadType): SquadTypeConfig {
    return {
      code: st.code,
      name: st.name,
      description: st.description || '',
      participant_type: this.mapParticipantType(st.participant_type),
      composed_of: this.mapParticipantType(st.composed_of),
      players_per_unit: st.players_per_unit,
      min_units: st.min_units,
      max_units: st.max_units ?? undefined,
      min_players: st.min_players ?? undefined,
      max_players: st.max_players ?? undefined,
      is_default: st.is_default,
      icon: st.icon ?? undefined,
      unitLabelSingular: st.unit_label_singular ?? undefined,
      unitLabelPlural: st.unit_label_plural ?? undefined,
    };
  }

  private mapMovementType(mt: BackendMovementType): MovementTypeConfig {
    return {
      code: mt.code,
      name: mt.name,
      description: mt.description,
      applicableCompetitionTypes: mt.applicable_competition_types,
      minTables: mt.min_tables,
      maxTables: mt.max_tables ?? undefined,
      supportsOdd: mt.supports_odd,
      isDefault: mt.is_default,
      icon: mt.icon ?? undefined,
      features: mt.features,
    };
  }

  private mapParticipantType(type: string): ParticipantType {
    const map: Record<string, ParticipantType> = {
      individual: ParticipantType.INDIVIDUAL,
      pair: ParticipantType.PAIR,
      team: ParticipantType.TEAM
    };
    return map[type] || ParticipantType.INDIVIDUAL;
  }

  private mapStatus(status: string): CompetitionTypeStatus {
    const map: Record<string, CompetitionTypeStatus> = {
      available: CompetitionTypeStatus.AVAILABLE,
      beta: CompetitionTypeStatus.BETA,
      planned: CompetitionTypeStatus.PLANNED,
      deprecated: CompetitionTypeStatus.DEPRECATED
    };
    return map[status] || CompetitionTypeStatus.AVAILABLE;
  }

  private getDefaultIcon(): string {
    // Generic fallback only. Per-discipline icons come from the backend (icon field).
    return '🏆';
  }

  /**
   * Get scoring system label from backend data (SINGLE SOURCE OF TRUTH).
   */
  private getScoringSystemLabel(code: string): string {
    if (this.discipline.scoring_systems && this.discipline.scoring_systems.length > 0) {
      const system = this.discipline.scoring_systems.find(ss => ss.code === code);
      if (system) return system.name;
    }
    return code; // Return code as fallback
  }

  /**
   * Get scoring system description from backend data (SINGLE SOURCE OF TRUTH).
   */
  private getScoringSystemDescription(code: string): string {
    if (this.discipline.scoring_systems && this.discipline.scoring_systems.length > 0) {
      const system = this.discipline.scoring_systems.find(ss => ss.code === code);
      if (system) return system.description;
    }
    return ''; // Return empty as fallback
  }
}

// =============================================================================
// UI CONFIGURATION FOR DISCIPLINE-SPECIFIC RENDERING
// =============================================================================

export interface DisciplineUIConfig {
  color: string;
  iconComponent?: React.ComponentType;
  customValidation?: (
    config: Record<string, unknown>,
    competitionType: BackendCompetitionType
  ) => ValidationResult;
  competitionDetailConfig?: Partial<CompetitionDetailConfig>;
}

// NOTE: Per-discipline UI configuration (color, terminology, booking labels,
// navigation cards, sections) is the SINGLE SOURCE OF TRUTH on the BACKEND.
// It is delivered via `discipline.ui_config` and mapped by
// BackendDisciplineAdapter.buildUIConfigFromBackend(). Adding a new discipline
// is therefore zero-touch on the frontend.

// =============================================================================
// UNIFIED REGISTRY
// =============================================================================

class UnifiedDisciplineRegistry {
  private adapters: Map<string, BackendDisciplineAdapter> = new Map();
  private initialized: boolean = false;
  private initPromise: Promise<void> | null = null;
  private coreCompatVerdict: CoreCompatibilityVerdict | null = null;

  /**
   * Initialize registry by fetching disciplines from backend
   */
  async initialize(forceRefresh = false): Promise<void> {
    if (this.initialized && !forceRefresh) {
      return;
    }

    if (this.initPromise && !forceRefresh) {
      return this.initPromise;
    }

    this.initPromise = this.doInitialize(forceRefresh);
    return this.initPromise;
  }

  private async doInitialize(forceRefresh: boolean): Promise<void> {
    try {
      const disciplines = await competitionPluginService.fetchDisciplines(forceRefresh);

      this.adapters.clear();

      // --- FE↔BE core-compatibility check ---
      const issues: CoreCompatibilityIssue[] = [];
      let observedBeCore: string | null = null;

      for (const discipline of disciplines) {
        if (discipline.has_implementation) {
          // UI config is derived from discipline.ui_config (backend) inside the adapter.
          const adapter = new BackendDisciplineAdapter(discipline);
          this.adapters.set(discipline.code, adapter);
        }

        // Capture the first non-null core_version seen (all disciplines share the same BE core).
        if (discipline.core_version && !observedBeCore) {
          observedBeCore = discipline.core_version;
        }

        // core_version absent → older BE → treat as compatible (do NOT warn).
        if (!discipline.core_version) continue;

        const majorOk = isMajorCompatible(discipline.core_version, EXPECTED_CORE_VERSION);
        const beMarkedIncompat = discipline.core_compatible === false;

        if (!majorOk || beMarkedIncompat) {
          const reason = beMarkedIncompat && discipline.core_compat_reason
            ? discipline.core_compat_reason
            : `MAJOR version mismatch — BE core ${discipline.core_version}, FE expects ${EXPECTED_CORE_VERSION}`;

          issues.push({ code: discipline.code, reason });
          console.warn(
            `[unified-registry] Core compatibility issue for discipline "${discipline.code}": ${reason}`
          );
        }
      }

      this.coreCompatVerdict = {
        expected: EXPECTED_CORE_VERSION,
        beCore: observedBeCore,
        compatible: issues.length === 0,
        issues,
      };
      // --- end compat check ---

      this.initialized = true;
      console.debug(`Unified registry initialized with ${this.adapters.size} disciplines`);
    } catch (error) {
      console.error('Failed to initialize unified registry:', error);
      throw error;
    }
  }

  /**
   * Get plugin adapter by discipline code
   */
  get(code: string): DisciplinePlugin | undefined {
    return this.adapters.get(code);
  }

  /**
   * Get all registered plugin adapters
   */
  getAll(): DisciplinePlugin[] {
    return Array.from(this.adapters.values());
  }

  /**
   * Get all discipline codes
   */
  getCodes(): string[] {
    return Array.from(this.adapters.keys());
  }

  /**
   * Check if discipline is registered
   */
  has(code: string): boolean {
    return this.adapters.has(code);
  }

  /**
   * Check if registry is initialized
   */
  isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Return the FE↔BE core-compatibility verdict computed during the last initialize().
   * Returns null if the registry has not been initialized yet.
   *
   * Verdict logic:
   *  - If BE does not expose core_version (older backend) → compatible: true, issues: []
   *  - If BE core MAJOR !== FE EXPECTED_CORE_VERSION MAJOR → issue logged + added here
   *  - If BE marks core_compatible === false → issue logged + added here
   *  - Never throws; caller must check `compatible` field
   */
  getCoreCompatibility(): CoreCompatibilityVerdict | null {
    return this.coreCompatVerdict;
  }

  /**
   * Get registry statistics
   */
  getStats() {
    const plugins = this.getAll();
    return {
      totalPlugins: plugins.length,
      totalCompetitionTypes: plugins.reduce(
        (sum, p) => sum + p.getCompetitionTypes().length, 0
      ),
      pluginCodes: this.getCodes(),
      source: 'backend-synced'
    };
  }

  /**
   * Refresh from backend
   */
  async refresh(): Promise<void> {
    return this.initialize(true);
  }

  /**
   * Clear registry
   */
  clear(): void {
    this.adapters.clear();
    this.initialized = false;
    this.initPromise = null;
    this.coreCompatVerdict = null;
  }
}

// Singleton instance
export const unifiedDisciplineRegistry = new UnifiedDisciplineRegistry();

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get plugin for a discipline (async - ensures initialization)
 */
export async function getPluginAsync(code: string): Promise<DisciplinePlugin | undefined> {
  await unifiedDisciplineRegistry.initialize();
  return unifiedDisciplineRegistry.get(code);
}

/**
 * Get all plugins (async - ensures initialization)
 */
export async function getAllPluginsAsync(): Promise<DisciplinePlugin[]> {
  await unifiedDisciplineRegistry.initialize();
  return unifiedDisciplineRegistry.getAll();
}

/**
 * Get competition types for discipline (async)
 */
export async function getCompetitionTypesAsync(disciplineCode: string): Promise<CompetitionTypeConfig[]> {
  const plugin = await getPluginAsync(disciplineCode);
  return plugin?.getCompetitionTypes() || [];
}

/**
 * Refresh registry from backend
 */
export async function refreshRegistry(): Promise<void> {
  return unifiedDisciplineRegistry.refresh();
}

export default unifiedDisciplineRegistry;
